import { useEffect, useRef, useState } from "react";
import { Terminal, Play, Pause, AlertTriangle } from "lucide-react";

export default function PseudoExploit({ pe }) {
  const [running, setRunning] = useState(false);
  const [typed, setTyped] = useState("");
  const idxRef = useRef(0);
  const code = pe?.code || "";

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      idxRef.current = Math.min(idxRef.current + 6, code.length);
      setTyped(code.slice(0, idxRef.current));
      if (idxRef.current >= code.length) {
        clearInterval(id);
        setRunning(false);
      }
    }, 18);
    return () => clearInterval(id);
  }, [running, code]);

  const reset = () => {
    idxRef.current = 0;
    setTyped("");
    setRunning(true);
  };

  if (!pe) return null;

  if (pe.refused) {
    return (
      <section
        data-testid="pseudo-exploit"
        className="panel p-6 md:p-7 fade-up delay-3"
      >
        <div className="overline mb-2">Pseudo-exploit</div>
        <div className="flex items-start gap-3 text-slate-700">
          <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <p className="text-sm leading-relaxed">
            {pe.explanation ||
              "The model declined to generate a pseudo-exploit for this CVE because the educational value is outweighed by misuse risk."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="pseudo-exploit"
      className="panel p-6 md:p-7 fade-up delay-3"
    >
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="overline">Sanitized pseudo-exploit</div>
          <h3 className="font-display text-xl text-slate-900 mt-1">
            What the attack *shape* looks like
          </h3>
          <p className="text-slate-500 text-xs mt-2 max-w-2xl">
            Illustrative only. Payloads are redacted (
            <code className="font-mono text-violet-700">&lt;REDACTED&gt;</code>);
            this is intentionally non-functional. Use it to recognize the
            pattern in code reviews and logs.
          </p>
        </div>
        <button
          onClick={running ? () => setRunning(false) : reset}
          data-testid="pseudo-run"
          className="flex items-center gap-2 bg-violet-50 text-violet-700 border border-violet-200 rounded-md px-3 py-1.5 text-xs font-mono hover:bg-violet-100 transition-colors"
        >
          {running ? (
            <>
              <Pause className="h-3 w-3" /> Pause
            </>
          ) : (
            <>
              <Play className="h-3 w-3" /> Simulate
            </>
          )}
        </button>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-md overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
          <Terminal className="h-3.5 w-3.5 text-violet-700" />
          <span className="font-mono text-xs text-slate-600">
            pseudo-exploit.{(pe.language || "txt").toLowerCase()}
          </span>
          <span className="ml-auto text-[10px] font-mono text-amber-700 border border-amber-200 bg-amber-50 px-2 py-0.5 rounded-md">
            SANITIZED
          </span>
        </div>
        <pre
          data-testid="pseudo-code"
          className="font-mono text-xs leading-relaxed p-4 text-slate-800 whitespace-pre-wrap min-h-[180px] max-h-[480px] overflow-y-auto"
        >
          {typed || code}
          {running && <span className="caret">&nbsp;</span>}
        </pre>
      </div>

      {pe.explanation && (
        <div className="mt-4">
          <div className="overline mb-2">Walkthrough</div>
          <p className="text-slate-700 text-sm leading-relaxed">
            {pe.explanation}
          </p>
        </div>
      )}
    </section>
  );
}
